import * as React from 'react'

type Data = {
  buffer: null | ArrayBuffer
  isLoading: boolean
  loadError: string | null
}

type Actions = {
  clearFile: () => void
  setFile: (file: File) => void
  setFileUrl: (fileUrl?: string) => void
}

type Props = {
  defaultFileUrl?: string
  children: (data: Data, actions: Actions) => React.ReactNode
}

const fetchModelBuffer = async (modelFile: string, signal: AbortSignal) => {
  const response = await fetch(modelFile, { signal })

  if (!response.ok) {
    throw new Error(`Could not load model URL: ${response.status}`)
  }

  return response.arrayBuffer()
}

/**
 * Manages the model buffer
 */
export const FileContainer = (props: Props) => {
  const [fileUrl, setFileUrl] = React.useState<string | undefined>(props.defaultFileUrl)
  const [isLoading, setLoadingState] = React.useState(typeof fileUrl === 'string')
  const [buffer, setBuffer] = React.useState<null | ArrayBuffer>(null)
  const [loadError, setLoadError] = React.useState<string | null>(null)

  React.useEffect(() => {
    setFileUrl(props.defaultFileUrl)
  }, [props.defaultFileUrl])

  /** Loads default model file and saves it to state */
  const loadingDemo = React.useCallback(async (modelFile: string, signal: AbortSignal) => {
    setLoadingState(true)
    setBuffer(null)
    setLoadError(null)

    try {
      const nextBuffer = await fetchModelBuffer(modelFile, signal)

      if (!signal.aborted) {
        setBuffer(nextBuffer)
      }
    } catch (error) {
      if (!signal.aborted) {
        const errorMessage = error instanceof Error ? error.message : 'Could not load model URL.'

        setBuffer(null)
        setLoadError(errorMessage)
      }
    } finally {
      if (!signal.aborted) {
        setLoadingState(false)
      }
    }
  }, [])

  /** Loads file to buffer */
  const loadFile = (file: File) => {
    const fileReader = new FileReader()

    fileReader.addEventListener('load', () => {
      setBuffer(fileReader.result as ArrayBuffer)
      setLoadError(null)
      setLoadingState(false)
    })

    fileReader.addEventListener('error', () => {
      setLoadError('Could not read the selected file.')
      setLoadingState(false)
    })

    setLoadingState(true)
    setBuffer(null)
    setLoadError(null)
    fileReader.readAsArrayBuffer(file)
  }

  const clearFile = React.useCallback(() => {
    setFileUrl(undefined)
    setLoadingState(false)
    setBuffer(null)
    setLoadError(null)
  }, [])

  React.useEffect(() => {
    if (typeof fileUrl === 'string') {
      const abortController = new AbortController()

      void loadingDemo(fileUrl, abortController.signal)

      return () => abortController.abort()
    }
  }, [fileUrl, loadingDemo])

  return (
    <React.Fragment>
      {props.children(
        { buffer, isLoading, loadError },
        {
          clearFile,
          setFile: loadFile,
          setFileUrl
        }
      )}
    </React.Fragment>
  )
}
